import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import heroImage from '../../assets/images_of_victoria/IMG_2280.jpg';
import supportImage from '../../assets/images_of_victoria/IMG_2264 (1).jpg';

const outcomes = [
  {
    label: 'Confidence',
    description: 'Programs are designed to help children express themselves with joy, safety, and encouragement.',
  },
  {
    label: 'Coordination',
    description: 'Creative movement, hands-on practice, and guided repetition support both motor growth and daily skills.',
  },
  {
    label: 'Connection',
    description: 'Every activity is meant to build friendship, belonging, and stronger social development over time.',
  },
];

const programs = [
  {
    id: '01',
    name: 'Arts',
    summary: 'Creative expression that builds visual awareness, confidence, and eye-hand coordination.',
    body:
      'The Arts program gives children with Down Syndrome a positive outlet for creativity through painting, drawing, sculpture, and other hands-on activities. As they create, they strengthen eye-hand coordination, improve visual awareness, and gain confidence in what they can make and share.',
    details:
      'Victoria’s Place sees art as both expression and encouragement. Finished artwork can become a source of pride, while guided instruction helps each child feel supported at their own pace.',
  },
  {
    id: '02',
    name: 'Music',
    summary: 'Rhythm, memory, confidence, and joyful participation through sound and movement.',
    body:
      'Music can be one of the most effective forms of support for children with Down Syndrome. It offers repeated opportunities to build memory, hand-eye coordination, cognitive growth, self-esteem, and social connection in a way that feels joyful rather than clinical.',
    details:
      'At Victoria’s Place, music is not treated as background noise. It becomes a welcoming way to listen, move, participate, and feel successful in community with others.',
  },
  {
    id: '03',
    name: 'Sports',
    summary: 'Movement-based learning that supports muscle tone, resilience, and teamwork.',
    body:
      'Children with Down Syndrome are often encouraged to participate in sports early because physical activity can help support muscle tone, coordination, and confidence. Sports also create a natural setting for learning teamwork, persistence, and celebrating progress.',
    details:
      'Victoria’s Place approaches sports with patience. Breaking activities into smaller parts, adapting equipment when needed, and keeping the environment encouraging helps children learn with confidence over time.',
  },
  {
    id: '04',
    name: 'Fine Motor Skills',
    summary: 'Small-muscle activities that strengthen dexterity, grip, control, and practical coordination.',
    body:
      'Because low muscle tone and joint flexibility can affect the way fine motor skills develop, children often benefit from activities that strengthen smaller muscle groups through steady, repeated practice. That can include building, molding, sorting, shaping, and guided tactile play.',
    details:
      'Victoria’s Place uses creative tools such as clay, building pieces, and sensory materials to make skill-building feel engaging, productive, and age-appropriate.',
  },
  {
    id: '05',
    name: 'Reading',
    summary: 'Language and literacy support rooted in imagination, comprehension, and spoken confidence.',
    body:
      'Reading helps children with Down Syndrome build vocabulary, strengthen grammar knowledge, and improve spoken language skills. It also supports working memory and gives children more opportunities to connect ideas, words, and stories in ways that feel meaningful.',
    details:
      'At Victoria’s Place, reading is designed to feel fun and inviting. Games, guided practice, and imagination-led activities help children engage with language in ways that are both effective and enjoyable.',
  },
];

const familySupportPoints = [
  'Programs are built around encouragement, not pressure.',
  'Activities are designed to support both expression and developmental growth.',
  'Children are welcomed into a space that values joy, patience, and belonging.',
];

export default function Programs() {
  return (
    <div className="programs-page">
      <section className="programs-hero programs-bleed">
        <div className="programs-hero-media">
          <img src={heroImage} alt="Victoria's Place program moment" />
        </div>

        <motion.div
          className="programs-hero-inner"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
        >
          <div className="programs-hero-copy">
            <span className="eyebrow programs-eyebrow">Programs</span>
            <p className="programs-kicker">Creative, practical, family-centered support</p>
            <h1>Programs that help children grow, express themselves, and feel encouraged.</h1>
            <p className="programs-hero-lead">
              Victoria&apos;s Place believes children with Down Syndrome deserve positive outlets that build confidence,
              coordination, expression, and connection. Our programs create room for that growth through art, music,
              sports, fine motor activities, and reading.
            </p>

            <div className="programs-hero-actions">
              <Link to="/contact" className="button-primary tap-target">
                Ask about programs
              </Link>
              <Link to="/give" className="button-secondary tap-target">
                Support this work
              </Link>
            </div>
          </div>
        </motion.div>
      </section>

      <div className="page-shell programs-shell">
        <motion.section
          className="programs-outcomes"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
        >
          {outcomes.map((item) => (
            <article key={item.label} className="programs-outcome">
              <p className="home-section-label">{item.label}</p>
              <p>{item.description}</p>
            </article>
          ))}
        </motion.section>

        <section className="programs-story">
          <motion.div
            className="programs-story-copy"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <p className="home-section-label">What This Means</p>
            <h2>Programs are not just activities. They are positive outlets for growth.</h2>
            <p>
              Victoria&apos;s Place was built from a parent&apos;s desire to make sure children with Down Syndrome have
              a place where they can create, move, learn, and connect with others in a loving environment. Each program
              is meant to support development while also protecting joy, dignity, and belonging.
            </p>
            <div className="programs-support-list">
              {familySupportPoints.map((point) => (
                <div key={point} className="programs-support-item">
                  <span />
                  <p>{point}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.figure
            className="programs-story-photo"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
          >
            <img src={supportImage} alt="Child enjoying a program moment at Victoria's Place" />
          </motion.figure>
        </section>

        <section className="programs-list-section">
          <div className="programs-section-heading">
            <p className="home-section-label">Program Areas</p>
            <h2>The work is creative, practical, and built around real encouragement.</h2>
          </div>

          <div className="programs-list">
            {programs.map((program, index) => (
              <motion.article
                key={program.id}
                className="programs-item"
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.18 }}
                transition={{ duration: 0.45, delay: index * 0.04, ease: 'easeOut' }}
              >
                <div className="programs-item-index">{program.id}</div>
                <div className="programs-item-body">
                  <div className="programs-item-header">
                    <h3>{program.name}</h3>
                    <p>{program.summary}</p>
                  </div>
                  <div className="programs-item-copy">
                    <p>{program.body}</p>
                    <p>{program.details}</p>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </section>

        <motion.section
          className="programs-cta"
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.24 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div className="programs-cta-copy">
            <p className="home-section-label home-section-label--light">Support The Programs</p>
            <h2>Help keep these opportunities visible, welcoming, and consistent for families.</h2>
            <p>
              If you want to support the program work of Victoria&apos;s Place through partnership, volunteering, or
              giving, we would love to hear from you.
            </p>
          </div>

          <div className="programs-cta-actions">
            <Link to="/give" className="button-primary tap-target">
              Give now
            </Link>
            <Link to="/contact" className="button-secondary tap-target">
              Contact Victoria&apos;s Place
            </Link>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
